<!--
  The app's one button. Role model: F&B / Equipment page (DESIGN_SYSTEM.md §3).

  Variants
    primary   — dark grey, black on hover. ONE per page (or per dialog).
    secondary — white, hairline border. Everything else that is a real action.
    ghost     — no border; quiet in-line actions (clear, more…).
    danger    — red text/outline; destructive actions only.
  Sizes: sm (h-8) for toolbars and rows, md (h-9) default.
  `iconOnly` makes a square button — pass an aria-label.
-->
<template>
  <component
    :is="to ? 'router-link' : 'button'"
    :to="to"
    :type="to ? undefined : type"
    :disabled="to ? undefined : (disabled || loading)"
    :aria-disabled="disabled || loading || undefined"
    :class="classes"
    @click="onClick"
  >
    <svg v-if="loading" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="animate-spin shrink-0"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
    <slot />
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md'
  iconOnly?: boolean
  disabled?: boolean
  loading?: boolean
  type?: 'button' | 'submit' | 'reset'
  /** Render as a router-link instead of a <button>. */
  to?: string | Record<string, any>
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'secondary',
  size: 'md',
  iconOnly: false,
  disabled: false,
  loading: false,
  type: 'button',
  to: undefined,
})

const emit = defineEmits<{ click: [event: MouseEvent] }>()

function onClick(e: MouseEvent) {
  if (props.disabled || props.loading) { e.preventDefault(); return }
  emit('click', e)
}

const VARIANTS = {
  primary: 'bg-gray-800 text-white border-gray-800 hover:bg-black hover:border-black',
  secondary: 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:text-gray-900',
  ghost: 'bg-transparent text-gray-600 border-transparent hover:bg-gray-100 hover:text-gray-900',
  danger: 'bg-white text-red-700 border-red-200 hover:bg-red-50 hover:border-red-300',
} as const

const SIZES = {
  sm: { box: 'h-8 px-3 text-[13px] gap-1.5', icon: 'h-8 w-8' },
  md: { box: 'h-9 px-3.5 text-[13px] gap-1.5', icon: 'h-9 w-9' },
} as const

const classes = computed(() => [
  'inline-flex items-center justify-center shrink-0 rounded-md border font-semibold no-underline',
  'transition-colors duration-150 cursor-pointer select-none whitespace-nowrap',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400/50 focus-visible:ring-offset-1',
  VARIANTS[props.variant],
  props.iconOnly ? `${SIZES[props.size].icon} p-0` : SIZES[props.size].box,
  props.disabled || props.loading ? 'opacity-50 cursor-not-allowed pointer-events-none' : '',
])
</script>
